/**
 * Web Health heart: the twin of `HealthHeart.tsx`.
 *
 * The heart is one `currentColor` path (the AI Studio artwork); the signal
 * line is cut out of it through a luminance mask, so whatever sits behind the
 * heart shows through the line, on any surface. The CSS in global.css
 * (`.tabbio-health-heart[data-state="working"]`) breathes the body and draws
 * the signal on the compositor, with the timings from `health-heart-path.ts`;
 * at rest the heart sits in the text colour.
 *
 * Motion pauses when the heart is scrolled out of view or the tab is hidden,
 * and reduced motion shows a still heart in the working colour. Decorative:
 * the status text beside it carries the meaning.
 */
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import {
  HEALTH_HEART_BOX,
  HEALTH_HEART_DEFAULT_SIZE,
  HEALTH_HEART_PATH,
  HEALTH_HEART_VIEWBOX,
  HEALTH_SIGNAL_PATH,
  HEALTH_SIGNAL_STROKE_WIDTH,
} from './health-heart-path';
import type { HealthHeartProps } from './health-props';

export type { HealthHeartProps };

function useMotionPaused(ref: RefObject<Element | null>) {
  const [visible, setVisible] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibility = () => setPageVisible(!document.hidden);
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(([entry]) => setVisible(Boolean(entry?.isIntersecting)));
    if (ref.current) observer?.observe(ref.current);
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [ref]);

  return !visible || !pageVisible;
}

export function HealthHeart({ size = HEALTH_HEART_DEFAULT_SIZE, state, color }: HealthHeartProps) {
  const maskId = `tabbio-health-heart-${useId().replace(/:/g, '')}`;
  const ref = useRef<SVGSVGElement>(null);
  const paused = useMotionPaused(ref);

  return (
    <svg
      ref={ref}
      className="tabbio-health-heart"
      width={size}
      height={size}
      viewBox={HEALTH_HEART_VIEWBOX}
      aria-hidden="true"
      focusable="false"
      data-state={state}
      data-paused={paused ? 'true' : undefined}
      style={color ? { color } : undefined}
    >
      <defs>
        <mask
          id={maskId}
          x={HEALTH_HEART_BOX.x}
          y={HEALTH_HEART_BOX.y}
          width={HEALTH_HEART_BOX.width}
          height={HEALTH_HEART_BOX.height}
          maskUnits="userSpaceOnUse"
          maskContentUnits="userSpaceOnUse"
          style={{ maskType: 'luminance' }}
        >
          <rect
            x={HEALTH_HEART_BOX.x}
            y={HEALTH_HEART_BOX.y}
            width={HEALTH_HEART_BOX.width}
            height={HEALTH_HEART_BOX.height}
            fill="white"
          />
          <path
            className="tabbio-health-heart-signal"
            d={HEALTH_SIGNAL_PATH}
            pathLength={1}
            fill="none"
            stroke="black"
            strokeWidth={HEALTH_SIGNAL_STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </mask>
      </defs>
      <path
        className="tabbio-health-heart-body"
        d={HEALTH_HEART_PATH}
        fill="currentColor"
        mask={`url(#${maskId})`}
      />
    </svg>
  );
}

export default HealthHeart;
